import { Directive, Input, TemplateRef, ViewContainerRef, inject, effect } from '@angular/core';
import { AuthService } from '../keycloak/services/auth.service';

/**
 * Directiva estructural para el control de acceso basado en permisos (RBAC/PBAC).
 * * Esta directiva gestiona la renderización condicional de elementos del DOM basándose en
 * los privilegios del usuario autenticado. Si el usuario cumple con los criterios,
 * el elemento se añade al DOM; de lo contrario, se elimina por completo para mejorar
 * la seguridad y la experiencia de usuario.
 * * ### Ejemplo de uso:
 * ```html
 * <button *appHasPermit="'USER_EDIT'">Editar Usuario</button>
 * <div *appHasPermit="['ADMIN', 'SUPERVISOR']">Panel de Control</div>
 * ```
 * * @usageNotes
 * La directiva acepta un solo permiso (string) o un conjunto de ellos (string[]).
 * Utiliza una lógica de tipo **OR**: el elemento se muestra si el usuario posee 
 * **al menos uno** de los permisos proporcionados.
 */
// BUG FIX #10: Reactive directive using effect() to re-evaluate when permissions change
@Directive({
  selector: '[appHasPermit]',
  standalone: false,
})
export class HasPermitDirective {
  private authService = inject(AuthService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private hasView = false;
  private requiredPermissions: string[] = [];

  constructor() {
    effect(() => {
      const perms = this.authService.permissions();
      this.updateView(perms);
    });
  }

  @Input() set appHasPermit(permissions: string | string[]) {
    this.requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
  }

  private updateView(userPermissions: Set<string>) {
    const hasAccess = this.requiredPermissions.some((p) => userPermissions.has(p));

    if (hasAccess && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAccess && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}